<script setup lang="ts">
/**
 * Компактная панель личного подбора: одна строка-сводка, раскрывается по нажатию.
 * Режимы (сервер — источник истины): скрывать / предупреждать / не учитывать; «может содержать».
 * Подписи ограничений передаёт страница (профиль покупателя); сервер подбора их не отдаёт.
 */
import { computed, onMounted, useId } from "vue";
import { PERSONAL_SELECTION_MODES, type PersonalSelectionMode } from "@/api/nutrition";
import { useNutritionSelectionStore } from "@/stores/nutrition-selection";

const props = defineProps<{
  /** undefined — подписи ещё не загружены; [] — ограничений в профиле нет. */
  restrictionLabels?: string[];
}>();

const store = useNutritionSelectionStore();
const uid = useId();

const MODE_LABEL: Record<PersonalSelectionMode, string> = { hide: "Скрывать", warn: "Предупреждать", ignore: "Не учитывать" };
const MODE_HINT: Record<PersonalSelectionMode, string> = {
  hide: "Неподходящие и товары с неизвестным составом не показываются.",
  warn: "Товары видны с пометкой; перед первым добавлением каждого спросим подтверждение.",
  ignore: "Личные ограничения не применяются до конца сессии. Профиль не меняется.",
};
const MESSAGE: Record<string, string> = {
  conflict: "Настройки изменились в другой вкладке — показаны актуальные.",
  stale: "Состав или настройки изменились — проверьте товар ещё раз.",
  not_pending: "Подтверждение больше не требуется — проверьте товар ещё раз.",
  unauthorized: "Сессия покупателя не найдена. Обновите страницу.",
  unavailable: "Не удалось связаться с магазином. Попробуйте ещё раз.",
  invalid: "Запрос отклонён. Обновите страницу.",
  not_found: "Товар не найден.",
};

const noRestrictions = computed(() => Array.isArray(props.restrictionLabels) && props.restrictionLabels.length === 0);
const who = computed(() =>
  props.restrictionLabels?.length ? `Мои ограничения: ${props.restrictionLabels.join(", ")}` : "Мои ограничения");
const mode = computed(() => store.session?.mode ?? null);

onMounted(() => { if (store.status === "idle") void store.load(); });

function choose(m: PersonalSelectionMode) { if (!store.busy) void store.setMode(m); }

function onKey(e: KeyboardEvent, i: number) {
  const n = PERSONAL_SELECTION_MODES.length;
  let next = -1;
  if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % n;
  else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + n) % n;
  else if (e.key === "Home") next = 0;
  else if (e.key === "End") next = n - 1;
  if (next < 0) return;
  e.preventDefault();
  choose(PERSONAL_SELECTION_MODES[next]);
  const group = (e.currentTarget as HTMLElement | null)?.parentElement;
  (group?.querySelectorAll<HTMLElement>('[role="radio"]')[next])?.focus();
}
</script>

<template>
  <section class="vd-card px-3 py-2" aria-label="Личный подбор">
    <p v-if="noRestrictions" data-testid="ps-summary" class="text-sm text-[color:var(--vd-ink-2)]">
      Личные ограничения не указаны — показываем весь ассортимент.
    </p>

    <div v-else-if="store.status === 'error'" data-testid="ps-error" role="alert" class="flex flex-wrap items-center gap-2 text-sm">
      <span style="color: var(--vd-hit-ink)">Не удалось загрузить настройки подбора — ограничения проверит корзина при добавлении.</span>
      <button type="button" class="vd-link min-h-[36px]" @click="store.load()">Повторить</button>
    </div>

    <p v-else-if="!store.session" data-testid="ps-summary" class="text-sm text-[color:var(--vd-ink-2)]" aria-live="polite">Загружаем настройки подбора…</p>

    <details v-else class="group">
      <summary data-testid="ps-summary" class="flex min-h-[40px] cursor-pointer list-none items-center gap-2 text-sm">
        <span class="min-w-0 flex-1 truncate font-semibold text-vd-ink">{{ who }}</span>
        <span class="vd-pill-brand shrink-0">{{ mode ? MODE_LABEL[mode] : "" }}</span>
        <span aria-hidden="true" class="shrink-0 text-[color:var(--vd-ink-2)] transition-transform group-open:rotate-180">▾</span>
      </summary>

      <div class="mt-2 space-y-2 pb-1">
        <div role="radiogroup" :aria-label="'Как учитывать мои ограничения'" class="grid grid-cols-3 gap-1">
          <button
            v-for="(m, i) in PERSONAL_SELECTION_MODES" :key="m" type="button" role="radio"
            :aria-checked="mode === m ? 'true' : 'false'" :tabindex="mode === m ? 0 : -1"
            :aria-describedby="`${uid}-hint-${m}`" :disabled="store.busy && store.pendingMode !== m"
            class="vd-chip min-h-[44px] w-full justify-center px-1 text-center text-[13px] leading-tight break-words"
            :class="{ 'vd-chip-active': mode === m, 'opacity-70': store.pendingMode === m }"
            @click="choose(m)" @keydown="onKey($event, i)"
          >{{ MODE_LABEL[m] }}</button>
        </div>
        <p v-for="m in PERSONAL_SELECTION_MODES" :id="`${uid}-hint-${m}`" :key="m" class="text-xs text-[color:var(--vd-ink-2)]" :class="{ 'sr-only': mode !== m }">
          {{ MODE_HINT[m] }}
        </p>

        <label class="flex min-h-[36px] items-center gap-2 text-sm text-vd-ink" :class="{ 'opacity-60': mode === 'ignore' }">
          <input
            data-testid="ps-traces" type="checkbox" class="h-5 w-5 accent-[color:var(--vd-brand)]"
            :checked="store.session.includeTraces" :disabled="mode === 'ignore' || store.busy"
            @change="store.setIncludeTraces(($event.target as HTMLInputElement).checked)"
          >
          Учитывать «может содержать следы»
        </label>

        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-xs text-[color:var(--vd-ink-2)]">Действует до конца сессии. Профиль и корзина не меняются.</span>
          <button type="button" class="vd-link min-h-[36px]" :disabled="store.busy" @click="store.endSession()">Завершить сессию</button>
        </div>
      </div>
    </details>

    <p v-if="store.message && store.status !== 'error' && !noRestrictions" role="status" class="mt-1 text-xs" style="color: var(--vd-hit-ink)">
      {{ MESSAGE[store.message] }}
    </p>
  </section>
</template>
